import createCache from '@emotion/cache';
import { alpha, createTheme, type Shadows, type Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { uiDirection } from './lib/i18n';

/** Emotion cache that flips CSS for right-to-left (MUI RTL guide). */
export const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
export const ltrCache = createCache({ key: 'muiltr', stylisPlugins: [prefixer] });

/** Used when the company has not picked a color (Google blue). */
export const DEFAULT_PRIMARY = '#0B57D0';

// ---------------------------------------------------------------------------
// Material 3 tokens, derived from ONE source color (the company's brand color).
// Neutrals keep a faint tint of the brand, like Google's dynamic color.
// ---------------------------------------------------------------------------
export interface M3Tokens {
  primary: string;
  onPrimary: string;
  primaryContainer: string;
  onPrimaryContainer: string;
  secondaryContainer: string;
  onSecondaryContainer: string;
  surface: string;
  surfaceContainerLowest: string;
  surfaceContainerLow: string;
  surfaceContainer: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;
  onSurface: string;
  onSurfaceVariant: string;
  outline: string;
  outlineVariant: string;
  inverseSurface: string;
  inverseOnSurface: string;
}

/** Subset of tones the shared form UI (libs/form-ui, same as the public form) reads. */
export interface FormTones {
  container: string;
  onContainer: string;
  soft: string;
  outline: string;
}
declare module '@mui/material/styles' {
  interface Palette {
    m3: M3Tokens;
    tones?: FormTones;
  }
  interface PaletteOptions {
    tones?: FormTones;
    m3?: M3Tokens;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    tonal: true;
  }
}

type RGB = [number, number, number];
const toRgb = (hex: string): RGB => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = ([r, g, b]: RGB) => `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
/** mix `a` toward `b` by t (0 → a, 1 → b) */
const mix = (a: string, b: string, t: number) => {
  const x = toRgb(a);
  const y = toRgb(b);
  return toHex([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
};

export function m3Tokens(source: string): M3Tokens {
  // a calmer version of the brand for neutrals (keeps hue, drops most chroma)
  const tint = mix(source, '#8E9199', 0.55);
  return {
    primary: source,
    onPrimary: '#FFFFFF',
    primaryContainer: mix(source, '#FFFFFF', 0.84),
    onPrimaryContainer: mix(source, '#000000', 0.62),
    secondaryContainer: mix(source, '#FFFFFF', 0.76),
    onSecondaryContainer: mix(source, '#000000', 0.66),
    surface: mix(source, '#FFFFFF', 0.97),
    surfaceContainerLowest: '#FFFFFF',
    surfaceContainerLow: mix(tint, '#FFFFFF', 0.93),
    surfaceContainer: mix(source, '#FFFFFF', 0.93),
    surfaceContainerHigh: mix(source, '#FFFFFF', 0.9),
    surfaceContainerHighest: mix(tint, '#FFFFFF', 0.82),
    onSurface: '#1F1F1F',
    onSurfaceVariant: '#444746',
    outline: '#747775',
    outlineVariant: mix(tint, '#FFFFFF', 0.7),
    inverseSurface: '#303030',
    inverseOnSurface: '#F2F2F2',
  };
}

/** Google's own elevation shadows (Material key + ambient light). */
const KEY = 'rgba(60,64,67,.3)';
const AMB = 'rgba(60,64,67,.15)';
const ELEVATION = [
  'none',
  `0 1px 2px 0 ${KEY}, 0 1px 3px 1px ${AMB}`,
  `0 1px 2px 0 ${KEY}, 0 2px 6px 2px ${AMB}`,
  `0 1px 3px 0 ${KEY}, 0 4px 8px 3px ${AMB}`,
  `0 2px 3px 0 ${KEY}, 0 6px 10px 4px ${AMB}`,
  `0 4px 4px 0 ${KEY}, 0 8px 12px 6px ${AMB}`,
];
const shadows = Array.from({ length: 25 }, (_, i) => ELEVATION[Math.min(i, 5)]) as Shadows;

/** Shape scale (px). Pick by component size — never one radius for everything. */
export const RADIUS = { xs: 4, sm: 8, md: 12, lg: 16, xl: 28, full: 999 } as const;

const FONT = '"Roboto", "Noto Sans Arabic", "IBM Plex Sans Arabic", "Segoe UI", Tahoma, sans-serif';

export function createAppTheme(primaryColor?: string | null, direction: 'rtl' | 'ltr' = 'rtl') {
  const source = primaryColor && /^#[0-9A-Fa-f]{6}$/.test(primaryColor) ? primaryColor.toUpperCase() : DEFAULT_PRIMARY;
  const t = m3Tokens(source);

  return createTheme({
    direction,
    cssVariables: true,
    shape: { borderRadius: RADIUS.sm },
    shadows,
    palette: {
      mode: 'light',
      m3: t,
      tones: { container: t.primaryContainer, onContainer: t.onPrimaryContainer, soft: t.surfaceContainerLow, outline: t.outline },
      primary: { main: source, contrastText: '#FFFFFF' },
      secondary: { main: '#00639B' },
      success: { main: '#146C2E', light: '#C4EED0', dark: '#0D5224' },
      warning: { main: '#B26200', light: '#FFDDB3', dark: '#7A4100' },
      error: { main: '#B3261E', light: '#F9DEDC', dark: '#8C1D18' },
      info: { main: '#0B57D0', light: '#D3E3FD', dark: '#041E49' },
      text: { primary: t.onSurface, secondary: t.onSurfaceVariant },
      divider: t.outlineVariant,
      background: { default: t.surface, paper: '#FFFFFF' },
      action: { hover: alpha(t.onSurface, 0.06), selected: alpha(source, 0.1), focus: alpha(t.onSurface, 0.1) },
    },
    typography: {
      fontFamily: FONT,
      fontSize: 14,
      h1: { fontSize: '2.25rem', lineHeight: 1.25, fontWeight: 600 },
      h2: { fontSize: '2rem', lineHeight: 1.25, fontWeight: 600 },
      h3: { fontSize: '1.75rem', lineHeight: 1.3, fontWeight: 600 },
      h4: { fontSize: '1.5rem', lineHeight: 1.35, fontWeight: 600 },
      h5: { fontSize: '1.375rem', lineHeight: 1.4, fontWeight: 600 },
      h6: { fontSize: '1.125rem', lineHeight: 1.45, fontWeight: 600 },
      subtitle1: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 500 },
      subtitle2: { fontSize: '0.875rem', lineHeight: 1.45, fontWeight: 500 },
      body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.55 },
      caption: { fontSize: '0.75rem', lineHeight: 1.4, letterSpacing: 0 },
      overline: { fontSize: '0.6875rem', fontWeight: 500, letterSpacing: 0, textTransform: 'none' },
      button: { fontSize: '0.875rem', fontWeight: 500, letterSpacing: 0, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': { height: '100%', overflow: 'hidden' },
          body: { backgroundColor: t.surface, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
          '::selection': { backgroundColor: t.secondaryContainer },
          '*:focus-visible': { outlineColor: source },
        },
      },

      // ---------------- buttons ----------------
      MuiButtonBase: {
        defaultProps: { disableRipple: false },
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': { outline: `2px solid ${source}`, outlineOffset: 2 },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: RADIUS.full, paddingInline: 24, minHeight: 40, whiteSpace: 'nowrap' },
          sizeSmall: { minHeight: 32, paddingInline: 16, fontSize: '0.8125rem' },
          sizeLarge: { minHeight: 48, paddingInline: 28, fontSize: '0.9375rem' },
          text: { paddingInline: 12 },
          outlined: ({ ownerState }) => ({
            ...(ownerState.color === 'primary' || !ownerState.color ? { borderColor: t.outline, color: source } : {}),
          }),
          contained: { '&:hover': { boxShadow: ELEVATION[1] } },
          startIcon: { marginInlineStart: -6, marginInlineEnd: 8, '& > *:nth-of-type(1)': { fontSize: 18 } },
          endIcon: { '& > *:nth-of-type(1)': { fontSize: 18 } },
        },
        variants: [
          {
            props: { variant: 'tonal' },
            style: {
              backgroundColor: t.secondaryContainer,
              color: t.onSecondaryContainer,
              '&:hover': { backgroundColor: mix(t.secondaryContainer, t.onSecondaryContainer, 0.08), boxShadow: ELEVATION[1] },
              '&.Mui-disabled': { backgroundColor: alpha(t.onSurface, 0.12), color: alpha(t.onSurface, 0.38) },
            },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: alpha(t.onSurface, 0.08) } },
        },
      },
      MuiFab: {
        defaultProps: { color: 'primary' },
        styleOverrides: {
          root: { borderRadius: RADIUS.lg, boxShadow: ELEVATION[3], textTransform: 'none' },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { borderRadius: RADIUS.full, gap: 0 },
          grouped: {
            borderColor: t.outline,
            '&:not(:first-of-type)': { marginInlineStart: -1 },
          },
          firstButton: { borderStartStartRadius: RADIUS.full, borderEndStartRadius: RADIUS.full },
          lastButton: { borderStartEndRadius: RADIUS.full, borderEndEndRadius: RADIUS.full },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            color: t.onSurface,
            paddingInline: 16,
            minHeight: 40,
            '&.Mui-selected': {
              backgroundColor: t.secondaryContainer,
              color: t.onSecondaryContainer,
              '&:hover': { backgroundColor: mix(t.secondaryContainer, t.onSecondaryContainer, 0.08) },
            },
          },
          sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: '0.8125rem' },
        },
      },

      // ---------------- surfaces ----------------
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' }, rounded: { borderRadius: RADIUS.lg } },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: RADIUS.lg, border: `1px solid ${t.outlineVariant}`, backgroundColor: '#FFFFFF', boxShadow: '0 1px 2px rgba(60,64,67,.06)' },
        },
      },
      MuiCardHeader: {
        defaultProps: { titleTypographyProps: { variant: 'subtitle1' } },
        styleOverrides: { root: { padding: '18px 20px 10px' }, action: { margin: 0, alignSelf: 'center' } },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: '12px 20px 20px', '&:last-child': { paddingBottom: 20 } } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: RADIUS.xl, backgroundColor: t.surfaceContainerLowest, boxShadow: ELEVATION[3] },
        },
      },
      MuiBackdrop: {
        styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgba(31,31,31,.32)' } } },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: '1.375rem', fontWeight: 400, lineHeight: 1.4, padding: '24px 24px 12px' } },
      },
      MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px' } } },
      MuiDialogContentText: { styleOverrides: { root: { color: t.onSurfaceVariant, fontSize: '0.875rem' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '20px 24px 24px', gap: 8, '& > :not(style) ~ :not(style)': { marginInlineStart: 0 } } } },
      MuiDrawer: { styleOverrides: { paper: { borderWidth: 0, backgroundColor: t.surface } } },
      MuiAppBar: { defaultProps: { elevation: 0, color: 'inherit' } },
      MuiDivider: { styleOverrides: { root: { borderColor: t.outlineVariant } } },

      // ---------------- menus / popovers / tooltips ----------------
      MuiMenu: {
        defaultProps: { disableScrollLock: true },
        styleOverrides: {
          paper: { borderRadius: RADIUS.sm, boxShadow: ELEVATION[2], backgroundColor: t.surfaceContainerLowest, minWidth: 180 },
          list: { paddingBlock: 8 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 44,
            paddingInline: 16,
            fontSize: '0.875rem',
            '&.Mui-selected': { backgroundColor: t.secondaryContainer, '&:hover': { backgroundColor: mix(t.secondaryContainer, t.onSecondaryContainer, 0.08) } },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            marginInline: 8,
            marginBlock: 2,
            width: 'auto',
            transition: 'background-color 120ms',
            '&.Mui-selected': {
              backgroundColor: t.secondaryContainer,
              color: t.onSecondaryContainer,
              '&:hover': { backgroundColor: mix(t.secondaryContainer, t.onSecondaryContainer, 0.08) },
            },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 36, color: t.onSurfaceVariant } } },
      MuiListSubheader: { styleOverrides: { root: { fontSize: '0.75rem', fontWeight: 500, color: t.onSurfaceVariant, lineHeight: '36px', backgroundColor: 'transparent' } } },
      MuiPopover: {
        defaultProps: { disableScrollLock: true },
        styleOverrides: { paper: { borderRadius: RADIUS.lg, boxShadow: ELEVATION[2] } },
      },
      MuiTooltip: {
        defaultProps: { arrow: false, enterDelay: 400 },
        styleOverrides: {
          tooltip: { backgroundColor: t.inverseSurface, color: t.inverseOnSurface, fontSize: '0.75rem', fontWeight: 400, borderRadius: RADIUS.xs, padding: '4px 8px' },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: { root: { backgroundColor: t.inverseSurface, color: t.inverseOnSurface, borderRadius: RADIUS.xs, boxShadow: ELEVATION[3] } },
      },

      // ---------------- inputs ----------------
      MuiTextField: { defaultProps: { variant: 'outlined' } },
      MuiFormControl: { defaultProps: { variant: 'outlined' } },
      MuiSelect: {
        defaultProps: {
          variant: 'outlined',
          MenuProps: { PaperProps: { sx: { maxHeight: 44 * 7 + 16 } } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            backgroundColor: '#FFFFFF',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.outline },
            '&:hover:not(.Mui-disabled, .Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: t.onSurface },
            '&.Mui-disabled': { backgroundColor: alpha(t.onSurface, 0.04) },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: t.onSurfaceVariant } } },
      MuiFormHelperText: { styleOverrides: { root: { marginInline: 16 } } },
      MuiInputAdornment: { styleOverrides: { root: { color: t.onSurfaceVariant } } },
      MuiCheckbox: { styleOverrides: { root: { color: t.onSurfaceVariant } } },
      MuiRadio: { styleOverrides: { root: { color: t.onSurfaceVariant } } },
      MuiSwitch: {
        styleOverrides: {
          // Material 3 switch: 52×32 track, thumb grows when on
          root: { width: 52 + 24, height: 32 + 24, padding: 12 },
          track: { borderRadius: 16, opacity: 1, backgroundColor: t.surfaceContainerHighest, border: `2px solid ${t.outline}`, boxSizing: 'border-box' },
          switchBase: {
            padding: 20,
            color: t.outline,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#FFFFFF',
              padding: 16,
              '& + .MuiSwitch-track': { opacity: 1, backgroundColor: source, borderColor: source },
              '& .MuiSwitch-thumb': { width: 24, height: 24 },
            },
            '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.38 },
          },
          thumb: { width: 16, height: 16, boxShadow: 'none', transition: 'width 120ms, height 120ms' },
        },
      },

      // ---------------- chips / badges / misc ----------------
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }: { ownerState: { color?: string; variant?: string } }) => {
            const c = ownerState.color && ownerState.color !== 'default' ? ownerState.color : null;
            const filled = ownerState.variant !== 'outlined';
            return {
              borderRadius: RADIUS.sm,
              fontWeight: 500,
              ...(filled && !c ? { backgroundColor: t.surfaceContainerHigh, color: t.onSurfaceVariant } : {}),
              // soft tonal chips: tinted fill + dark text (status chips)
              ...(filled && c ? {
                backgroundColor: `color-mix(in srgb, var(--mui-palette-${c}-main) 13%, #fff)`,
                color: `color-mix(in srgb, var(--mui-palette-${c}-main) 78%, #000)`,
                '& .MuiChip-icon': { color: 'inherit' },
              } : {}),
              ...(!filled && !c ? { borderColor: t.outline, color: t.onSurfaceVariant } : {}),
            };
          },
          sizeSmall: { height: 26, borderRadius: 6, fontSize: '0.75rem' },
          label: { paddingInline: 10 },
          labelSmall: { paddingInline: 8 },
          iconSmall: { fontSize: 15, marginInlineStart: 6 },
          clickable: ({ ownerState }: { ownerState: { color?: string; variant?: string } }) =>
            ownerState.color === 'primary' && ownerState.variant !== 'outlined'
              ? { backgroundColor: t.secondaryContainer, color: t.onSecondaryContainer, '&:hover': { backgroundColor: mix(t.secondaryContainer, t.onSecondaryContainer, 0.08) } }
              : {},
        },
      },
      MuiBadge: { styleOverrides: { badge: { fontWeight: 500, fontSize: '0.6875rem', minWidth: 16, height: 16, padding: '0 4px' } } },
      MuiAvatar: { styleOverrides: { root: { fontWeight: 500 }, colorDefault: { backgroundColor: t.primaryContainer, color: t.onPrimaryContainer } } },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: RADIUS.md, alignItems: 'center', fontSize: '0.875rem' },
          standardWarning: { backgroundColor: '#FEF7E0', color: '#5F3A00' },
          standardInfo: { backgroundColor: t.primaryContainer, color: t.onPrimaryContainer },
          standardSuccess: { backgroundColor: '#E6F4EA', color: '#0D5224' },
          standardError: { backgroundColor: '#FCE8E6', color: '#8C1D18' },
          action: { paddingTop: 0, alignItems: 'center' },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: RADIUS.full, backgroundColor: t.surfaceContainerHighest }, bar: { borderRadius: RADIUS.full } },
      },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: t.surfaceContainerHigh }, rounded: { borderRadius: RADIUS.md } } },
      MuiTabs: {
        styleOverrides: { indicator: { height: 3, borderRadius: '3px 3px 0 0' } },
      },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 500, minHeight: 48 } } },
      MuiTableCell: {
        styleOverrides: { head: { fontWeight: 500, color: t.onSurfaceVariant }, root: { borderColor: t.outlineVariant } },
      },
      MuiBreadcrumbs: { styleOverrides: { separator: { color: t.outline } } },
      MuiLink: { defaultProps: { underline: 'hover' } },
      MuiImageListItem: { styleOverrides: { root: { borderRadius: RADIUS.md, overflow: 'hidden' } } },

      // ---------------- MUI X data grid ----------------
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 0,
            '--DataGrid-rowBorderColor': t.outlineVariant,
            '--DataGrid-containerBackground': '#FFFFFF',
            '--unstable_DataGrid-radius': `${RADIUS.lg}px`,
            fontSize: '0.875rem',
            color: t.onSurface,
          },
          columnHeader: { color: t.onSurfaceVariant, '&:focus, &:focus-within': { outline: 'none' } },
          columnHeaderTitle: { fontWeight: 500 },
          columnSeparator: { display: 'none' },
          cell: { '&:focus, &:focus-within': { outline: 'none' } },
          row: {
            '&:hover': { backgroundColor: alpha(t.onSurface, 0.04) },
            '&.Mui-selected': { backgroundColor: alpha(source, 0.08), '&:hover': { backgroundColor: alpha(source, 0.12) } },
          },
          toolbar: { padding: '10px 12px', minHeight: 60, borderBottom: `1px solid ${t.outlineVariant}`, gap: 4 },
          footerContainer: { borderTop: `1px solid ${t.outlineVariant}`, minHeight: 52 },
          overlay: { backgroundColor: 'rgba(255,255,255,.6)' },
          panel: { '& .MuiPaper-root': { borderRadius: RADIUS.md, boxShadow: ELEVATION[2] } },
          menu: { '& .MuiPaper-root': { borderRadius: RADIUS.sm } },
        },
      },
    },
  });
}

export type AppTheme = Theme;
/** The platform wears the FormTags identity: black and white. */
export const BRAND = '#111111';
export const theme = createAppTheme(BRAND, uiDirection());
